/**
 * BillingPage — Plan, Usage & Upgrade
 *
 * Shows the user's current plan, live token/cost usage against limits,
 * and lets them upgrade to Pro or Enterprise.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '../../components/layout';
import { Spinner } from '../../components/ui';
import {
  billingApi,
  PlanDefinition,
  Subscription,
  UsageSummary,
  ModelUsageEntry,
  PlanTier,
  BillingCycle,
} from '../../services/api';
import styles from './BillingPage.module.css';
import { CheckCircle, Zap, Building2, RefreshCw } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : String(n);

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;

const planIcon = (id: PlanTier) => {
  if (id === 'enterprise') return <Building2 size={20} />;
  if (id === 'pro') return <Zap size={20} />;
  return null;
};

// ──────────────────────────────────────────────────────────────────────────────
// Usage Meter
// ──────────────────────────────────────────────────────────────────────────────

interface MeterProps {
  label: string;
  used: number;
  limit: number;
  format: (n: number) => string;
  pct: number;
}

const UsageMeter: React.FC<MeterProps> = ({ label, used, limit, format, pct }) => {
  const unlimited = limit <= 0;
  const color = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#6366f1';

  return (
    <div className={styles.meter}>
      <div className={styles.meterHeader}>
        <span className={styles.meterLabel}>{label}</span>
        <span className={styles.meterValue}>
          {format(used)} / {unlimited ? '∞' : format(limit)}
        </span>
      </div>
      <div className={styles.meterBar}>
        <div
          className={styles.meterFill}
          style={{ width: unlimited ? '8%' : `${Math.min(100, pct)}%`, background: color }}
        />
      </div>
      {!unlimited && <div className={styles.meterPct}>{pct.toFixed(1)}% used</div>}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Plan Card
// ──────────────────────────────────────────────────────────────────────────────

interface PlanCardProps {
  plan: PlanDefinition;
  current: boolean;
  billingCycle: BillingCycle;
  onSelect: (plan: PlanTier) => void;
  upgrading: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, current, billingCycle, onSelect, upgrading }) => {
  const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
  const isFree = plan.id === 'free';

  return (
    <div className={`${styles.planCard} ${current ? styles.planCardCurrent : ''} ${plan.id === 'pro' ? styles.planCardHighlight : ''}`}>
      {plan.id === 'pro' && <div className={styles.popularBadge}>Most Popular</div>}

      <div className={styles.planHeader}>
        <div className={styles.planIconWrap}>{planIcon(plan.id)}</div>
        <div className={styles.planName}>{plan.name}</div>
        {current && <span className={styles.currentBadge}>Current</span>}
      </div>

      <div className={styles.planPrice}>
        {isFree ? (
          <span className={styles.planPriceNumber}>Free</span>
        ) : (
          <>
            <span className={styles.planPriceNumber}>${price}</span>
            <span className={styles.planPricePer}>/mo{billingCycle === 'annual' ? ' (billed annually)' : ''}</span>
          </>
        )}
      </div>

      <ul className={styles.featureList}>
        {plan.features.map((f, i) => (
          <li key={i} className={styles.featureItem}>
            <CheckCircle size={13} className={styles.featureCheck} />
            {f}
          </li>
        ))}
      </ul>

      {!current && !isFree && (
        <button
          className={styles.upgradeBtn}
          onClick={() => onSelect(plan.id)}
          disabled={upgrading}
        >
          {upgrading ? 'Upgrading…' : `Upgrade to ${plan.name}`}
        </button>
      )}
      {!current && isFree && (
        <button className={`${styles.upgradeBtn} ${styles.downgradeBtnStyle}`} onClick={() => onSelect(plan.id)} disabled={upgrading}>
          Downgrade to Free
        </button>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────

export const BillingPage: React.FC = () => {
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, planRes, usageRes] = await Promise.all([
        billingApi.getPlans(),
        billingApi.getPlan(),
        billingApi.getUsage(),
      ]);

      if (plansRes.success) setPlans(plansRes.data.plans);
      if (planRes.success) {
        setSubscription(planRes.data.subscription);
        setBillingCycle(planRes.data.subscription.billingCycle);
      }
      if (usageRes.success) setUsage(usageRes.data);
    } catch {
      showToast('Failed to load billing info', false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpgrade = async (plan: PlanTier) => {
    setUpgrading(true);
    try {
      const res = await billingApi.upgradePlan(plan, billingCycle);
      if (res.success) {
        setSubscription(res.data.subscription);
        showToast(res.data.message, true);
        // Reload usage after upgrade
        const usageRes = await billingApi.getUsage();
        if (usageRes.success) setUsage(usageRes.data);
      } else {
        showToast('Upgrade failed. Please try again.', false);
      }
    } catch {
      showToast('Upgrade failed. Please try again.', false);
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className={styles.loadingWrap}>
          <Spinner size="lg" label="Loading billing info…" />
        </div>
      </AppLayout>
    );
  }

  const currentPlan = subscription?.plan ?? 'free';

  return (
    <AppLayout>
      <div className={styles.page}>
        <div className={styles.content}>

          {/* Header */}
          <div className={styles.header}>
            <div>
              <h1 className={styles.title}>Plans & Billing</h1>
              <p className={styles.subtitle}>
                Manage your subscription and monitor token usage across BrainIQ's API keys.
              </p>
            </div>
            <button className={styles.refreshBtn} onClick={loadData} disabled={loading}>
              <RefreshCw size={15} className={loading ? styles.spinning : ''} />
            </button>
          </div>

          {/* Usage Section */}
          {usage && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Current Period Usage</h2>
              <p className={styles.sectionDesc}>
                Showing usage from{' '}
                <strong>{new Date(usage.periodStart).toLocaleDateString()}</strong> to{' '}
                <strong>{new Date(usage.periodEnd).toLocaleDateString()}</strong>{' '}
                via BrainIQ keys. Your own API keys are unlimited.
              </p>

              {usage.isOverLimit && (
                <div className={styles.overLimitBanner}>
                  Monthly quota exceeded — further requests via BrainIQ keys are paused until your period resets or you upgrade.
                </div>
              )}

              <div className={styles.metersGrid}>
                <UsageMeter
                  label="Tokens (BrainIQ Key)"
                  used={usage.serverKeyTokens}
                  limit={usage.limits.monthlyTokens}
                  format={fmt}
                  pct={usage.percentTokensUsed}
                />
                <UsageMeter
                  label="Cost (BrainIQ Key)"
                  used={usage.serverKeyCost}
                  limit={usage.limits.monthlyCost}
                  format={fmtUsd}
                  pct={usage.percentCostUsed}
                />
              </div>

              <div className={styles.usageStats}>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>BrainIQ Key Tokens</span>
                  <span className={styles.statValue}>{fmt(usage.serverKeyTokens)}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>BrainIQ Key Cost</span>
                  <span className={styles.statValue}>{fmtUsd(usage.serverKeyCost)}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Your Key Tokens</span>
                  <span className={styles.statValue}>{fmt(usage.userKeyTokens)}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Your Key Cost</span>
                  <span className={styles.statValue}>{fmtUsd(usage.userKeyCost)}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Total Requests</span>
                  <span className={styles.statValue}>{usage.totalRequests.toLocaleString()}</span>
                </div>
              </div>

              {/* Per-model breakdown table */}
              {usage.modelBreakdown && usage.modelBreakdown.length > 0 && (
                <div className={styles.modelBreakdown}>
                  <h3 className={styles.modelBreakdownTitle}>Usage by Model</h3>
                  <table className={styles.modelTable}>
                    <thead>
                      <tr>
                        <th className={styles.modelTableTh}>Model</th>
                        <th className={styles.modelTableTh}>Provider</th>
                        <th className={styles.modelTableTh}>Requests</th>
                        <th className={styles.modelTableTh}>Tokens</th>
                        <th className={styles.modelTableTh}>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.modelBreakdown.map((entry: ModelUsageEntry) => (
                        <tr key={entry.model} className={styles.modelTableRow}>
                          <td className={styles.modelTableTd}>
                            <span className={styles.modelName}>{entry.model}</span>
                          </td>
                          <td className={styles.modelTableTd}>
                            <span className={`${styles.providerBadge} ${styles[`provider_${entry.provider}`] || styles.provider_default}`}>
                              {entry.provider}
                            </span>
                          </td>
                          <td className={styles.modelTableTd}>{entry.requests.toLocaleString()}</td>
                          <td className={styles.modelTableTd}>{fmt(entry.tokens)}</td>
                          <td className={styles.modelTableTd}>{fmtUsd(entry.cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* Billing Cycle Toggle */}
          <div className={styles.cycleToggle}>
            <button
              className={`${styles.cycleBtn} ${billingCycle === 'monthly' ? styles.cycleBtnActive : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`${styles.cycleBtn} ${billingCycle === 'annual' ? styles.cycleBtnActive : ''}`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual <span className={styles.saveBadge}>Save ~17%</span>
            </button>
          </div>

          {/* Plan Cards */}
          <section className={styles.section}>
            <div className={styles.plansGrid}>
              {plans.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  current={currentPlan === plan.id}
                  billingCycle={billingCycle}
                  onSelect={handleUpgrade}
                  upgrading={upgrading}
                />
              ))}
            </div>
          </section>

          {/* Note */}
          <p className={styles.note}>
            Payment processing is handled via Stripe. By upgrading, you agree to our Terms of Service.
            Contact support for Enterprise invoicing.
          </p>
        </div>
      </div>

      {toast && (
        <div className={`${styles.toast} ${toast.ok ? styles.toastOk : styles.toastErr}`}>
          {toast.msg}
        </div>
      )}
    </AppLayout>
  );
};

export default BillingPage;
